<template>
  <v-container fluid grid-list-md>
    <h1>{{ $t('Application.cart') }}</h1>
    <v-data-table
      :headers="headers"
      :items="cart.items"
      :rows-per-page-items="rowsPerPageItems"
      :pagination.sync="state.pagination"
      :total-items = "state.pagination.totalItems"
      must-sort
      disable-initial-sort
      hide-actions
      class="elevation-1 mt-3"
      @update:pagination="onUpdatePagination"
    >

      <template slot="headerCell" slot-scope="props">
        <span>{{ $t(props.header.text) }}</span>
      </template>

      <template slot="items" slot-scope="props">
        <td v-if="columns.indexOf('itemnum') >=0">{{ props.item.itemnum }}</td>
        <td v-if="columns.indexOf('name') >=0">
          {{ props.item.name }}
          <template v-if="props.item.description">
            <br/>
            <small style="color: darkorange">{{props.item.description}}</small>
          </template>
        </td>
        <td class="text-xs-right" style="color: #2196F3" v-if="columns.indexOf('amount') >=0">
          <NumericInput :value="items[props.item.itemnum]"
                        button-width="21px"
                        :min="1"
                        style="width: 100px"
                        @input="onQtyChanged(props.item, $event)"
                        :values="getQuantityScale(props.item)"
                        v-if="!props.item.restricted"/>
          <span style="display: block; text-align: center" v-else>{{items[props.item.itemnum] ? items[props.item.itemnum] : props.item.amount}}</span>
        </td>
        <td class="text-xs-right" v-if="columns.indexOf('taxrate') >=0">{{ props.item.taxrate.toLocaleString($i18n.locale) }}</td>
        <td class="text-xs-right" v-if="columns.indexOf('sellingprice') >=0">{{ (props.item.amount == 0 ? 0 : props.item.nettotal / props.item.amount).toLocaleString($i18n.locale, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
        <td class="text-xs-right" v-if="columns.indexOf('nettotal') >=0">{{ props.item.nettotal.toLocaleString($i18n.locale, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
        <td class="text-xs-center">
          <v-btn :flat="true" @click="removeItem(props.item)" color="info" small v-if="!props.item.restricted" style="min-width: 40px">
            <v-icon>delete</v-icon>
          </v-btn>
        </td>
      </template>

      <template slot="footer" v-if="cart.items.length > 0 && (columns.indexOf('sellingprice') >=0 || columns.indexOf('taxrate') >=0 || columns.indexOf('nettotal') >=0)">
        <td class="grey subheading" :colspan="columns.length - (columns.indexOf('sellingprice') >=0 || columns.indexOf('taxrate') >=0 || columns.indexOf('nettotal') >=0 ? 2 : 0)"><strong>{{ $t('Common.total') }}</strong></td>
        <td class="grey text-xs-right caption" v-if="columns.indexOf('sellingprice') >=0 || columns.indexOf('taxrate') >=0 || columns.indexOf('nettotal') >=0">
          <div v-if="columns.indexOf('sellingprice') >=0">{{ $t('Common.netAmount')}} :</div>
          <div v-if="columns.indexOf('taxrate') >=0">{{ $t('Common.taxAmount')}} :</div>
          <div v-if="columns.indexOf('nettotal') >=0">{{ $t('Common.grossAmount')}} :</div></td>
        <td class="grey text-xs-right" v-if="columns.indexOf('sellingprice') >=0 || columns.indexOf('taxrate') >=0 || columns.indexOf('nettotal') >=0">
          <div v-if="columns.indexOf('sellingprice') >=0">{{ cart.netamount.toLocaleString($i18n.locale, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</div>
          <div v-if="columns.indexOf('taxrate') >=0">{{ cart.tax.toLocaleString($i18n.locale, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</div>
          <div v-if="columns.indexOf('nettotal') >=0">{{ cart.total.toLocaleString($i18n.locale, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</div>
        </td>
        <td class="grey text-xs-right"></td>
      </template>

    </v-data-table>

    <template v-if="cart.items.length > 0">
      <div class="mt-4">
        <span>{{$t('Addresses.addressShipping')}}: </span>
        <span>{{deliveryAddress}}</span>
      </div>
      <div class="mt-2">
        <v-btn class="ml-0" color="warning" @click="clearCart">{{ $t('Action.clearCart') }}</v-btn>
        <v-btn color="success" @click="openPlaceOrderDialog">{{ $t('Action.placeOrder') }}</v-btn>
      </div>
    </template>

    <div class="mt-4" v-if="footerLinks" v-html="$t(footerLinks)">
    </div>

    <ConfirmDialog
      v-if="placeOrderDialog"
      @close="closePlaceOrderDialog"
      :messages="placeOrderMessages"
      :confirmDisabled="!!placeOrderMessages.text && !termsAccepted"
      :width="$root.config.cartConfirmationFormWidth"
    >
      <template v-slot:text="{text}">
        <v-checkbox
          :rules="[v => !!v || '']"
          v-model="termsAccepted"
        >
          <template v-slot:label>
            <span  class="confirm-text" v-html="text"></span>
          </template>
        </v-checkbox>
      </template>
      <v-container v-if="confirmationForm">
        <custom-form
          :formElements="confirmationForm"
          ref="form"
        />
      </v-container>
    </ConfirmDialog>
  </v-container>
</template>

<script>
  import store from '../store'
  import NumericInput from '../components/NumericInput'
  import ConfirmDialog from '../components/ConfirmDialog'
  import CustomForm from '../components/CustomForm'

  import {getCountryName} from '../utils/index';

  export default {
    name: 'Cart',
    components: {
      NumericInput,
      ConfirmDialog,
      CustomForm
    },
    watch: {
      items: {
        handler: function (value) {
          this.refresh();
        },
        deep: true
      }
    },
    computed: {
      deliveryAddress() {
        if (this.$root.hasSession) {
          const {address, city, postalcode, country, phone} = this.$root.session;
          if (address && city && postalcode && country) {
            const countryName = getCountryName(country);
            let res = `${address}, ${postalcode} ${city}, ${this.$i18n.t(countryName)}`;
            if (phone) {
              res += `, tel: ${phone}`;
            }
            return res;
          }
        }
        return '';
      },
      state() {
        return store.cart;
      },
      placeOrderMessages() {
        const messages = this.$i18n.messages[this.$i18n.locale].Cart.PlaceOrder;
        if (messages.text) {
          messages.text = messages.text.replace('{0}', this.$root.config.placeOrderTermsOfService)
        }
        return messages;
      },
      confirmationForm() {
        const form = this.$root.config.cartConfirmationForm;
        console.log('Form', form)
        return form;
      },
      footerLinks() {
        const footer = this.$root.config.cartFooterLinks;
        console.log('CartFooterLinks', footer)
        return footer;
      }
    },
    data() {
      const columns = this.$root.config.cartColumns || 'itemnum name amount taxrate sellingprice nettotal'.split(' ');

      return {
        columns,
        headers: [
          {text: 'Common.itemNum', value: 'itemnum', align: 'left'},
          {text: 'Common.itemName', value: 'name', align: 'left'},
          {text: 'Common.quantity', value: 'amount', align: 'center', width: '100px'},
          {text: 'Common.taxPercent', value: 'taxrate', align: 'right'},
          {text: 'Common.itemPrice', value: 'sellingprice', align: 'right'},
          {text: 'Common.total', value: 'nettotal', align: 'right'},
          {text: 'Action.remove', align: 'center', sortable: false, width: '40px'}
        ].filter(x => !x.value || columns.indexOf(x.value) >= 0),
        cart: {
          netamount: 0,
          tax: 0,
          total: 0,
          taxAmounts: {},
          taxes: {},
          items: []
        },
        rowsPerPageItems: [{ "text": "$vuetify.dataIterator.rowsPerPageAll", "value": -1 }],
        placeOrderDialog: false,
        termsAccepted: false,
      };
    },
    props: {
      items: {
        type: Object
      }
    },
    mounted() {
      console.log('Mount Cart');
      this.refresh();
    },
    methods: {
      refresh() {
        this.$http.post(this.$root.route('cart'), {items: this.items}).then(
          response => {
            console.log('Success', response);
            this.cart = response.body.result;
            this.cart.items.forEach(item => {
              item.total = Math.round((item.nettotal + item.taxamount) * 1000000) / 1000000
            })
            this.state.pagination.totalItems = response.body.result.count;
          },
          response => {
            console.log('Fail', response);
          });
      },
      onUpdatePagination(paginationObject) {
        Object.keys(paginationObject).forEach(key => this.state.pagination[key] = paginationObject[key]);
        this.refresh()
      },
      clearCart() {
        this.$emit('clear');
      },
      openPlaceOrderDialog() {
        this.placeOrderDialog = true;
      },
      async closePlaceOrderDialog(confirmed) {
        this.placeOrderDialog = false;
        if (confirmed) {
          let keyValueList = [];
          if (this.$refs.form) {
            if (!this.$refs.form.validate()) {
              this.placeOrderDialog = true;
              return;
            }
            keyValueList = await this.$refs.form.getData()
          }
          const data = (keyValueList || []).reduce((p, c) => {p[c.key] = c.value; return p;}, {})
          this.placeOrder(data);
        }
        this.termsAccepted = false;
      },
      placeOrder(data) {
        
        const formData = new FormData()
        for (let fieldName in data) {
          const value = data[fieldName]
          if (value instanceof File) {
            formData.append(`form[${fieldName}]`, value, value.name)
          } else {
            formData.append(`form[${fieldName}]`, value)
          }
        }

        for (let itemField in this.items) {
          formData.append(`items[${itemField}]`, this.items[itemField])
        }

        this.$http.post(this.$root.route('order'), formData).then(
          response => {
            console.log('Success', response);
            this.clearCart();
          },
          response => {
            console.log('Fail', response);
          });
      },
      removeItem(item) {
        this.onQtyChanged(item, 0)
      },
      onQtyChanged(item, value) {
        this.$set(this.items, item.itemnum, value);
        this.refresh();
      },
      getQuantityScale(item) {
        let scale = (item.quantityscale || '').split(',').map(x => x.trim());
        if (!this.$root.config.autoContinueQuantityScale)
          return scale;
        let reverse = scale.reverse();
        if (reverse[0] && reverse[1]) {
          let stepLo = parseInt(reverse[1], 10);
          let stepHi = parseInt(reverse[0], 10);
          let stepSize = stepHi - stepLo;
          for (let i=stepHi+stepSize; i<20000; i += stepSize)
            scale.push(i);
        }
        return scale;
      }
    }
  }
</script>

<style lang="scss">
  th.column.sortable.text-xs-center {
    padding-left: 8px;
  }

  .confirm-text {
    font-size: 14px;
  }

</style>
